import React, { forwardRef, useRef, useState, useEffect } from 'react';
import {
    Box,
    FormControl,
    FormLabel,
    Input,
    Text,
    Image,
} from '@chakra-ui/react';
import { BASE_URL } from "../../../config";
import { useTranslation } from "react-i18next";
import { useAppToast } from "../../../hooks/useAppToast";
import ImageCropModal from '../ImageCropModal/ImageCropModal';

const FileUploadInput = forwardRef(({ onFileSelect, isEditing = true, coverImage = '', coverImageName = '' }, ref) => {
    const { t } = useTranslation("default");
    const toast = useAppToast();
    const inputRef = useRef(null);
    const [fileName, setFileName] = React.useState(coverImage);
    const [previewUrl, setPreviewUrl] = useState('');
    const [rawImageSrc, setRawImageSrc] = useState('');
    const [isCropModalOpen, setIsCropModalOpen] = useState(false);
    const baseURL = BASE_URL;

    useEffect(() => {
        if (coverImage && typeof coverImage === 'string') {
            const isFullUrl = coverImage.startsWith('http');
            setPreviewUrl(isFullUrl ? coverImage : `${baseURL}/${coverImage}`);
        } else if (!coverImage) {
            setPreviewUrl('');
        }
    }, [coverImage]);

    const handleClick = () => {
        inputRef.current?.click();
    };

    const handleChange = (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        const validTypes = ['image/jpeg', 'image/png'];
        if (file && !validTypes.includes(file.type)) {
            toast({ title: t('image.wrong_format'), status: 'error' });
            return;
        }
        if (file) {
            setFileName(file.name);
            setRawImageSrc(URL.createObjectURL(file));
            setIsCropModalOpen(true);
        }
    };

    const handleCropComplete = (croppedFile) => {
        const croppedPreviewUrl = URL.createObjectURL(croppedFile);
        setPreviewUrl(croppedPreviewUrl);
        onFileSelect?.(croppedFile);
    };

    const handleCropClose = () => {
        setIsCropModalOpen(false);
    };

    return (
        <FormControl>
            <FormLabel fontWeight="400" fontFamily="body">{t('image.message')}</FormLabel>
            <Box
                role="button"
                as="div"
                onClick={handleClick}
                cursor="pointer"
                border="1px solid #D9D9D9"
                borderRadius={'12px'}
                w="full"
                h="40px"
                textAlign="left"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                px="10px"
                bg="white"
            >
                {coverImageName || fileName || 'Wybierz plik'}
                <Box as="span">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 13V14C1 14.7956 1.31607 15.5587 1.87868 16.1213C2.44129 16.6839 3.20435 17 4 17H14C14.7956 17 15.5587 16.6839 16.1213 16.1213C16.6839 15.5587 17 14.7956 17 14V13M13 5L9 1M9 1L5 5M9 1V13" stroke="#1E1517" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </Box>
            </Box>
            {previewUrl && (
                <Box
                    mt={3}
                    border="1px solid #ECE4E4"
                    borderRadius={'12px'}
                    overflow="hidden"
                    w="full"
                    style={{ aspectRatio: '16 / 9' }}
                >
                    <Image src={previewUrl} w="full" h="full" objectFit="contain" bg="#F8ECEF" />
                </Box>
            )}
            <Input
                type="file"
                accept=".jpg,.png"
                display="none"
                ref={inputRef}
                onChange={handleChange}
                disabled={!isEditing}
            />
            <Text mt="1" fontSize="sm" color="gray.600">
                {t('image.info')}
            </Text>
            <ImageCropModal
                isOpen={isCropModalOpen}
                onClose={handleCropClose}
                imageSrc={rawImageSrc}
                onCropComplete={handleCropComplete}
            />
        </FormControl>
    );
});

export default FileUploadInput;
